import React from 'react';
import { ArrowLeft, Chevron } from '../../components/Glyph';
import { day } from '../../lib/when';
import CompanyLogo from '../../components/CompanyLogo';
import { OnHandLogo } from '../../components/brand/OnHandLogo';
import { StatCard } from './countEntryHelpers';
import { isUncounted } from './lineState';
import { pressable } from '../../lib/pressable';

// Read-only view rendered when a count is in `submitted` or `approved` status.
// Items are grouped by item_number_raw and rolled up into a single summary row;
// items with multiple locations expand to show per-location sub-rows.
//
// `ownerBanner` is an optional element rendered above the stat cards (used to
// show admin override / cross-rep view banners from the parent CountEntry).
export default function LockedView({ count, items, totalItems, totalUnits, flagged, navigate, ownerBanner }) {
  const statusLabel = count.status === 'approved' ? 'Approved' : count.status === 'submitted' ? 'Submitted' : 'View Only';
  const statusCls   = count.status === 'approved' ? 'pill-app' : count.status === 'submitted' ? 'pill-ip' : 'pill-sub'; // guide 11.4: Submitted gold, In progress blue
  const [expandedItems, setExpandedItems] = React.useState({});

  function toggleExpand(itemNum) {
    setExpandedItems(prev => ({ ...prev, [itemNum]: !prev[itemNum] }));
  }

  // Group items by item_number_raw for rolled-up display
  const groupedItems = React.useMemo(() => {
    const groups = {};
    for (const item of items) {
      const key = item.item_number_raw?.toUpperCase() || '';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    // Sort each group by location_seq
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => (a.location_seq || 1) - (b.location_seq || 1));
    }
    // Return sorted by item number
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  /*
    **No rail since rebrand step 4**, 17 September 2026. The brand guide's 11.7
    removes any leftover sidebar, and this screen had the last one: a column
    holding the logo, the company's logo and Back to Dashboard. The logos are in
    a glass top bar now, like every other screen, and Back to Dashboard is beside
    the sheet's name and status, the same button doing the same thing.
  */
  return (
    <div className="oh-phone-shell">
      <header className="oh-top is-held">
        <div className="oh-topbar">
          <div className="oh-topbar-brand">
            <OnHandLogo iconSize={118 * 101 / 386.58} />
            <span className="oh-topbar-co">
              <span aria-hidden="true" style={{ width: 1, height: 24, background: 'var(--border)', flexShrink: 0 }} />
              <CompanyLogo height={24} maxWidth={132} fallbackToName={false} />
            </span>
          </div>
        </div>
      </header>
      <div className="oh-held-body">
        <div className="locked-page">
          <div className="locked-head oh-ground-panel">
            <div style={{ minWidth: 0 }}>
              <h1>{count.warehouse?.name}</h1>
              {/* **"Submitted not yet" is what this used to say** when a sheet had
                  been approved without a submitted date recorded, which reads as
                  two states at once beside a chip saying Submitted. The guide's
                  voice: say what happened, and say nothing rather than something
                  contradictory. */}
              <p>{count.cycle?.name}{count.submitted_at ? ' - Submitted ' + day(count.submitted_at) : ''}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className={'pill ' + statusCls}>{statusLabel}</span>
              {/* A button, not a span. It was neither reachable by keyboard nor
                  announced as something you can press, and its arrow was the
                  typed character U+2190. */}
              <button type="button" onClick={() => navigate(-1)} className="btn btn-outline">
                <ArrowLeft size={14} />
                Back to Dashboard
              </button>
            </div>
          </div>
          {ownerBanner}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24, maxWidth: 480 }}>
            {/* **The colour tints have gone, 11 September 2026**, job 27 phase 3.
                Each of these used to paint its whole card in a status colour,
                blue, gold and red or green. The brand guide's 9.4 card is white
                with an edge, and its rule 5 keeps a colour for a dot, a fill or
                an icon rather than for a surface somebody reads a number off. */}
            <StatCard num={totalItems} label="Items" />
            <StatCard num={totalUnits} label="Units" />
            <StatCard num={flagged}    label="Flagged" />
          </div>
          <div className="card locked-table" style={{ padding: 0, overflow: 'hidden' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: '4%' }}></th>
                  <th style={{ width: '18%' }}>Item #</th>
                  <th style={{ width: '40%' }}>Description</th>
                  <th style={{ width: '18%' }}>Vendor</th>
                  <th style={{ width: '12%', textAlign: 'right' }}>Total Qty</th>
                  <th style={{ width: '8%' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {groupedItems.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 32, fontStyle: 'italic' }}>No items recorded in this count.</td></tr>
                ) : groupedItems.map(([itemNum, entries]) => {
                  const primary      = entries[0];
                  const hasLocations = entries.length > 1 || entries.some(e => e.location);
                  const totalQty     = entries.reduce((sum, e) => sum + (e.quantity || 0), 0);
                  /*
                    **Blank is not zero here either**, which is the fourth place
                    in this screen it was. Group E found it while drawing: a
                    sheet somebody else is still working on can be opened
                    read-only, and every line they have not been to yet printed
                    as `0`, so a manager reading it saw a finished count with
                    empty shelves rather than a count still being done.
                  */
                  const blanks       = entries.filter(isUncounted).length;
                  const isExpanded   = expandedItems[itemNum];
                  const allNotes     = entries.map(e => e.notes).filter(Boolean);
                  return (
                    <React.Fragment key={itemNum}>
                      {/* Rolled-up summary row */}
                      <tr style={{ background: primary.not_in_catalog ? 'var(--warning-bg)' : 'transparent', cursor: hasLocations ? 'pointer' : 'default' }}
                          {...pressable(hasLocations ? () => toggleExpand(itemNum) : undefined, { role: undefined, 'aria-expanded': hasLocations ? !!isExpanded : undefined })}>
                        <td className="locked-chev" style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 12 }}>
                          {hasLocations && (
                            <Chevron dir={isExpanded ? 'down' : 'right'} size={13} style={{ margin: '0 auto' }} />
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 700, color: primary.not_in_catalog ? 'var(--warning-ink)' : 'var(--action-text)' }}>{primary.item_number_raw}</div>
                          {hasLocations && (
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{entries.length} location{entries.length !== 1 ? 's' : ''}</div>
                          )}
                        </td>
                        <td data-label="Description">{primary.description_raw}</td>
                        <td data-label="Vendor" style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{primary.vendor_raw || '--'}</td>
                        <td data-label="Total Qty" style={{ textAlign: 'right', fontWeight: 800, fontSize: 15 }}>
                          {blanks === entries.length
                            ? <span style={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: 12 }}>Not counted</span>
                            : totalQty}
                          {blanks > 0 && blanks < entries.length && (
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)' }}>
                              {blanks} not counted
                            </div>
                          )}
                        </td>
                        <td>
                          {allNotes.length > 0 && (
                            <span title={allNotes.join(' | ')} style={{ fontSize: 11, color: 'var(--link)', cursor: 'help' }}>Note</span>
                          )}
                          {primary.not_in_catalog && (
                            <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--warning-bg)', color: 'var(--warning-ink)', padding: '2px 6px', borderRadius: 4, display: 'block' }}>Custom</span>
                          )}
                        </td>
                      </tr>
                      {/* Location sub-rows, shown when expanded */}
                      {hasLocations && isExpanded && entries.map((entry, idx) => (
                        <tr key={entry.id} className="locked-sub" style={{ background: 'var(--info-bg)', borderLeft: '3px solid var(--info-bg)' }}>
                          <td />
                          <td style={{ paddingLeft: 24 }}>
                            {entry.location ? (
                              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--info-ink)', background: 'var(--info-bg)', border: '1px solid var(--info-bg)', borderRadius: 4, padding: '2px 6px', letterSpacing: 0.5 }}>
                                {entry.location}
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No location</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            {idx === 0 ? entry.description_raw : ''}
                          </td>
                          <td />
                          <td data-label="Qty" style={{ textAlign: 'right', fontWeight: 700, color: isUncounted(entry) ? 'var(--text-secondary)' : 'var(--text-secondary)' }}>
                            {isUncounted(entry) ? 'Not counted' : entry.quantity}
                          </td>
                          <td>
                            {entry.notes && (
                              <span title={entry.notes} style={{ fontSize: 11, color: 'var(--action-text)', cursor: 'help' }}>Note</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
